import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FolderTree, HardDrive, Info, Loader2, RefreshCw } from 'lucide-react';
import Modal from './Modal';
import { formatFolderInfoBytes, requestFolderInfo } from './folderInfo';

const targetKey = (target) => String(typeof target === 'string' ? target : target?.key || '');

const targetName = (target) => String(typeof target === 'object' ? target?.name || '' : '').trim();

const canonicalKey = (value) => {
    const key = String(value || '');
    return key.endsWith('/') ? key.slice(0, -1) : key;
};

const countLabel = (value, singular, plural = `${singular}s`) => {
    const count = Number.isFinite(Number(value)) ? Number(value) : 0;
    return `${count.toLocaleString('en-US')} ${count === 1 ? singular : plural}`;
};

const FolderMetrics = ({ stats, total = false }) => (
    <dl className={`folder-info-modal-metrics${total ? ' total' : ''}`}>
        <div className="folder-info-modal-metric" role="group" aria-label="Folder size">
            <dt><HardDrive size={16} aria-hidden="true" /> Size</dt>
            <dd>{formatFolderInfoBytes(stats?.total_bytes)}</dd>
        </div>
        <div className="folder-info-modal-metric" role="group" aria-label="Total items">
            <dt><Info size={16} aria-hidden="true" /> Total items</dt>
            <dd>{Number(stats?.child_count || 0).toLocaleString('en-US')}</dd>
        </div>
        <div className="folder-info-modal-metric" role="group" aria-label="Contents">
            <dt><FolderTree size={16} aria-hidden="true" /> Contents</dt>
            <dd>
                {countLabel(stats?.file_count, 'file')}
                <span aria-hidden="true"> · </span>
                {countLabel(stats?.folder_count, 'folder')}
            </dd>
        </div>
    </dl>
);

/**
 * Loads and displays recursive size and child counts for one or more folders.
 */
const FolderInfoModal = ({
    caseId,
    targets = [],
    onClose,
    requestFolderInfoFn = requestFolderInfo,
}) => {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [result, setResult] = useState(null);
    const requestRef = useRef({ id: 0, controller: null, signature: '', inFlight: false });

    const normalizedTargets = useMemo(() => (Array.isArray(targets) ? targets : [])
        .map((target) => ({ key: targetKey(target), name: targetName(target) }))
        .filter((target) => target.key), [targets]);
    const folderKeySignature = normalizedTargets.map((target) => target.key).join('\u0000');
    const targetNames = useMemo(() => new Map(
        normalizedTargets.map((target) => [canonicalKey(target.key), target.name]).filter(([, name]) => name)
    ), [normalizedTargets]);

    const load = useCallback(async () => {
        const folderKeys = folderKeySignature ? folderKeySignature.split('\u0000') : [];
        const nextSignature = `${caseId}\u0000${folderKeySignature}`;
        if (requestRef.current.inFlight && requestRef.current.signature === nextSignature) {
            return;
        }
        requestRef.current.controller?.abort();

        if (folderKeys.length === 0) {
            setLoading(false);
            setResult(null);
            setError('Select at least one folder to calculate its information.');
            return;
        }

        const controller = new AbortController();
        const requestId = requestRef.current.id + 1;
        requestRef.current = { id: requestId, controller, signature: nextSignature, inFlight: true };
        setLoading(true);
        setError('');
        setResult(null);

        try {
            const nextResult = await requestFolderInfoFn({
                caseId,
                folderKeys,
                signal: controller.signal,
            });
            if (requestRef.current.id === requestId && !controller.signal.aborted) {
                setResult(nextResult);
            }
        } catch (requestError) {
            if (requestRef.current.id === requestId && !controller.signal.aborted && requestError?.name !== 'AbortError') {
                setError(requestError?.message || 'The folder information could not be calculated.');
            }
        } finally {
            if (requestRef.current.id === requestId && !controller.signal.aborted) {
                requestRef.current.inFlight = false;
                setLoading(false);
            }
        }
    }, [caseId, folderKeySignature, requestFolderInfoFn]);

    useEffect(() => {
        load();
        return () => requestRef.current.controller?.abort();
    }, [load]);

    const close = () => {
        requestRef.current.controller?.abort();
        onClose?.();
    };

    const rows = Array.isArray(result?.folders) ? result.folders : [];
    const selectionCount = normalizedTargets.length;
    const showTotals = selectionCount > 1;

    return (
        <Modal
            title="Folder information"
            instructions={selectionCount === 1
                ? 'Calculates everything stored inside this folder.'
                : `Calculates everything stored inside ${selectionCount.toLocaleString('en-US')} selected folders.`}
            onClose={close}
            footer={
                <div className="modal-footer-actions">
                    {error && normalizedTargets.length > 0 && (
                        <button type="button" className="action alt" onClick={load} disabled={loading}>
                            {loading ? <Loader2 size={16} className="folder-info-modal-spinner" aria-hidden="true" /> : <RefreshCw size={16} aria-hidden="true" />}
                            {loading ? 'Calculating…' : 'Try again'}
                        </button>
                    )}
                    <button type="button" className="action" onClick={close}>Close</button>
                </div>
            }
        >
            <div
                className="modal-content-wrapper folder-info-modal"
                role="region"
                aria-label="Folder information content"
                aria-live="polite"
            >
                {loading && (
                    <div className="folder-info-modal-status" role="status">
                        <Loader2 size={22} className="folder-info-modal-spinner" aria-hidden="true" />
                        <div>
                            <strong>Calculating folder information…</strong>
                            <span className="small-text">Large folders may take a moment.</span>
                        </div>
                    </div>
                )}

                {!loading && error && (
                    <div className="folder-info-modal-alert" role="alert">
                        <Info size={20} aria-hidden="true" />
                        <div>
                            <strong>Folder information is unavailable</strong>
                            <span className="small-text">{error}</span>
                        </div>
                    </div>
                )}

                {!loading && !error && result && rows.length === 0 && (
                    <div className="folder-info-modal-empty" role="status">
                        <FolderTree size={24} aria-hidden="true" />
                        <div>
                            <strong>No folder information was returned.</strong>
                            <span className="small-text">The folders may be empty or no longer available.</span>
                        </div>
                    </div>
                )}

                {!loading && !error && rows.length > 0 && (
                    <div className="folder-info-modal-results">
                        <div className="folder-info-modal-list" role="list" aria-label="Calculated folders">
                            {rows.map((folder, index) => {
                                const displayName = targetNames.get(canonicalKey(folder.key)) || folder.name || `Folder ${index + 1}`;
                                return (
                                    <section className="folder-info-modal-row" role="listitem" key={folder.key || `${displayName}-${index}`}>
                                        <div className="folder-info-modal-row-header">
                                            <FolderTree size={18} aria-hidden="true" />
                                            <strong>{displayName}</strong>
                                        </div>
                                        <FolderMetrics stats={folder} />
                                    </section>
                                );
                            })}
                        </div>

                        {showTotals && (
                            <section className="folder-info-modal-total" aria-label="Selected folder totals">
                                <h3>Total</h3>
                                <FolderMetrics stats={result.totals} total />
                            </section>
                        )}

                        {result.warnings?.length > 0 && (
                            <div className="folder-info-modal-warning" role="alert">
                                <Info size={18} aria-hidden="true" />
                                <div>
                                    <strong>Some information could not be calculated</strong>
                                    <ul>
                                        {result.warnings.map((warning, index) => <li key={`${warning}-${index}`}>{warning}</li>)}
                                    </ul>
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </Modal>
    );
};

export default FolderInfoModal;
